import { API_BASE_URL } from '../config/runtime';

// Helper function to get full image URL from animal ID
export const getImageUrl = (animalId: string | null | undefined): string => {
  if (!animalId) {
    return '/default-dog.svg'; // Default image served from frontend
  }

  // Use the animal image API endpoint with cache busting
  const timestamp = Date.now();
  return `${API_BASE_URL}/animals/${animalId}/image?t=${timestamp}`;
};

// Helper function to handle API responses
const handleResponse = async (response: Response) => {
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || data.error || 'API request failed');
  }

  return data;
};

// Public API (no authentication required)
export const publicAPI = {
  // Get animals available for adoption
  getAnimals: async (params?: {
    page?: number;
    limit?: number;
    size?: string;
    gender?: string;
    age?: string;
  }) => {
    const queryString = params ? new URLSearchParams(params as any).toString() : '';
    const response = await fetch(`${API_BASE_URL}/public/animals?${queryString}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    return handleResponse(response);
  },

  // Get single animal details
  getAnimal: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/public/animals/${id}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    return handleResponse(response);
  },

  // Get animals statistics (total available for adoption)
  getAnimalsStats: async () => {
    const response = await fetch(`${API_BASE_URL}/public/animals/stats`, {
      headers: { 'Content-Type': 'application/json' },
    });
    return handleResponse(response);
  },



  // Get public system settings
  getSystemSettings: async () => {
    const response = await fetch(`${API_BASE_URL}/public/settings`, {
      headers: { 'Content-Type': 'application/json' },
    });
    return handleResponse(response);
  },

  // Submit contact form
  // Submit contact form
  submitContact: async (contactData: FormData | {
    name: string;
    email: string;
    phone?: string;
    subject: string;
    message: string;
  }) => {
    const isFormData = contactData instanceof FormData;

    // For FormData, let the browser set the Content-Type header with the boundary
    const headers: HeadersInit = isFormData ? {} : { 'Content-Type': 'application/json' };
    const body = isFormData ? contactData : JSON.stringify(contactData);

    const response = await fetch(`${API_BASE_URL}/public/contact/submit`, {
      method: 'POST',
      headers,
      body,
    });
    return handleResponse(response);
  },

  // Submit adoption application
  submitAdoptionApplication: async (applicationData: {
    animalId: string;
    adopterFirstName: string;
    adopterLastName: string;
    adopterEmail: string;
    adopterPhone: string;
    adopterAddress: string;
    livingArrangement: string;
    hasExperience: boolean;
    hasOtherPets: boolean;
    reasonForAdoption: string;
    additionalInfo?: string;
  }) => {
    const response = await fetch(`${API_BASE_URL}/public/adoption-application`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(applicationData),
    });
    return handleResponse(response);
  }
};

// Animal interface for the website
export interface PublicAnimal {
  id: string;
  name: string;
  gender: 'male' | 'female';
  age: string;
  size: 'mic' | 'mediu' | 'mare';
  color: string;
  description: string;
  image: string;
  status: 'available';
  vaccinated: boolean;
  neutered: boolean;
  chipped: boolean;
  entryDate: string;
  totalInvested: number;
}



// Contact form interface
export interface ContactForm {
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
}
